import React, { useEffect, useState, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Button, Form, ProgressBar } from "react-bootstrap";
import { IKImage } from "imagekitio-react";
import _ from "lodash";
import cx from "classnames";

import { FileUploadButton } from "components/Buttons";
import { 
  deleteBrandKitIcons, 
  initiateBrandKitIconsUpload, 
  invalidateBrandKitIconsDeleteError, 
  invalidateBrandKitIconsUploadError, 
  searchBrandKitIcons, 
  updateBrandKitIconsUploadProgress, 
  uploadBrandKitIcons 
} from "actions/brandKit/icons";

import Header from "../../components/Header";
import InfiniteScroll from "components/InfiniteScroll";
import DeleteModal from "../../components/DeleteModal";
import EmptyMessage from "../../components/EmptyMessage";
import { HandleMediaUploadError, MediaErrorMessage } from "../../components/ErrorToast";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";

import styles from "../brandkit.elements.module.scss";
import { createMixpanelEvent } from "actions/mixpanel";
import { MIXPANEL_BRANDKIT_CREATED } from "constants/mixpanelEvents";

const Options = [
  {
    name: "icons",
    label: "Icons",
  }
];

const defaultContentImage = "/public/cleverstory/v2/static/default_card.jpg";


const IconElement = () => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    active: 'icons',
    selected: []
  })
  const multiple = true;
  const [showError, setShowError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toggleBrandElements, setToggleBrandElements] = useState(false);
  const [uploadInstances, setUploadInstances] = useState([]);

  const paletteIds = useSelector((state) => _.get(state, "brandKit.activated.items", []));
  const uploads = useSelector(state => _.get(state, 'brandKit.icons.uploads')) || [];
  const iconsList = useSelector(state => _.get(state, 'brandKit.icons.items')) || [];
  const dataFetched = useSelector(state => _.get(state, 'brandKit.icons.fetched'));
  const loading = useSelector(state => _.get(state, 'brandKit.icons.loading'));
  const deleting = useSelector(state => _.get(state, 'brandKit.icons.deleting'));
  const pagination = useSelector(state => _.get(state, 'brandKit.icons.pagination'));
  const error = useSelector(state => _.get(state, 'brandKit.icons.error'));
  const uploadingFail = useSelector(state => _.get(state, 'brandKit.icons.uploadError', false));
  const errorType = useSelector(state => _.get(state,'brandKit.icons.errorType'));
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;

  const paletteId = useMemo(
    () => _.find(paletteIds, { type: "ICONS" })?.id,
    [paletteIds]
  );

  const enabled = useMemo(
    () => _.find(paletteIds, { type: "ICONS" })?.enabled,
    [paletteIds]
  );

  useEffect(()=>{
    if (enabled != toggleBrandElements) {
      setToggleBrandElements(enabled)
    }
  },[enabled])

  useEffect(() => {
    if(paletteIds){
      handleSearchIcons();
    }
  }, [paletteIds])

  useEffect(() => {
    if (!loading && showDeleteModal && !deleting) {
      setShowDeleteModal(false);
      setState(state => {
        return {
          ...state,
          selected: []
        }
      })
    }
  }, [deleting]);

  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  const handleTabChange = (active) => {
    setState(state => {
      return {
        ...state,
        active: active
      }
    })
  }

  const handleSearchIcons = () => {
    if (paletteId) {
      const request = {
      palette: paletteId,
      target: null,
      mediaTypes: ['image'],
      page: 0,
      limit: 20,
      sort: 'modifiedDate',
      order: 'desc'
      };

      dispatch(searchBrandKitIcons(request));
   }
  }

  const loadMoreIcons = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }

    if (paletteId) {
      const request = {
      palette: paletteId,
      target: null,
      page: pagination.currentPage + 1,
      mediaTypes: ['image'],
      limit: 20,
      sort: 'modifiedDate',
      order: 'desc'
      };

      dispatch(searchBrandKitIcons(request));
    }  
  }

  const handleSelect = (icons) => {
    var updated = [...state.selected];

    if (multiple) {
      const exists = updated.find(m => m.id === icons.id) != null;
      if (exists) {
        updated = updated.filter(m => m.id !== icons.id);
      } else {
        updated = [...updated, icons];
      }
    } else {
      updated = [icons];
    }
    
    setState(state => {
      return {
        ...state,
        selected: updated
      }
    })
  }

  const handleUploadInstanceChange = (instance) => {
    setUploadInstances((state) => {
      return state.map(item => {
        if (item.files[0].id === instance.files[0].id) {
          return instance
        }

        return item;
      })
    });
  }

  const handleErrorClose = () => {
    setShowError(false);
    dispatch(invalidateBrandKitIconsDeleteError());
  }

  const handleUploadErrorClose = () => {
    dispatch(invalidateBrandKitIconsUploadError());
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <div className={cx(styles.categoriesList, "px-2")}>
          {Options.map((data) => (
            <span
              onClick={() => handleTabChange(data.name)}
              className={cx(
                styles.icons,
                state.active === data.name && styles.iconSelected
              )}
              key={data.name}
            >
              {data.label}
            </span>
          ))}
        </div>
        <div className={styles.liner}></div>
        <div className={styles.mainContainer}>
          <Header
            selectedCategory={state.active}
            toggleBrandElements={toggleBrandElements}
            setToggleBrandElements={setToggleBrandElements}
            deleteHandler={() => setShowDeleteModal(true)}
            selectedItems={state.selected}
            loading={loading}
            paletteId={paletteId}
          >
            <IconsUploadButton
              paletteId={paletteId}
              state={state}
              onInstanceChange={handleUploadInstanceChange} 
            />
          </Header>

          <div className={styles.mainContentWrapper}>
            <Component
             loading={loading}
             hasMoreItems={hasMoreItems}
             state={state}
             iconsList={iconsList}
             loadMoreIcons={loadMoreIcons}
             uploads={uploads}
             handleClick={handleSelect}
             dataFetched={dataFetched}
             showDeleteModal={showDeleteModal}
             setShowDeleteModal={setShowDeleteModal}
             deleting={deleting}
             onInstanceChange={handleUploadInstanceChange} 
            />
           {showError && _.size(error) > 0 &&
             <MediaErrorMessage message='The media cannot be deleted as it exists in one or more pages.' onClose={handleErrorClose} />
           }
           {uploadingFail && 
             <HandleMediaUploadError message={errorType} onClose={handleUploadErrorClose}/>
           }
          </div>
        </div>
      </div>
    </div>
  );
};

const Component = ({
  loading,
  hasMoreItems,
  state,
  iconsList,
  loadMoreIcons,
  uploads,
  handleClick,
  dataFetched,
  showDeleteModal,
  setShowDeleteModal,
  deleting,
  onInstanceChange
}) => {

  const dispatch = useDispatch();

  if (dataFetched && uploads.length == 0 && iconsList?.length == 0) {
    return (
      <div className={styles.emptyMsgContainer}>
        <EmptyMessage/>
      </div>
    )
  }

  if (loading && iconsList?.length === 0) {
    return (
      <div className={styles.loaderContainer}>
        <label>Just a moon-ute... Scanning for icons... </label>
      </div>
    )
  }

  const handleDelete = () => {
    const ids = state?.selected?.map(item => item.id);

    if (ids.length > 0) {
      dispatch(deleteBrandKitIcons(ids))
    }
  };

  return (
    <InfiniteScroll
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={() => loadMoreIcons()}
    >
      <div className={styles.fontsWrapper}>
        {uploads?.map((upload) => {
          return (
            <MediaUploadIndicator
              key={upload.id}
              state={state}
              upload={upload}
              instance={upload.instance}
              onDimensionChange={(dimension) => {
                upload.instance.files[0].dimension = dimension;
                onInstanceChange(upload.instance);
              }}
          />
          );
        })}
        {state.active == 'icons' && iconsList.map(icons => {
          if (icons.type !== 'image') {
            return null;
          }
          return (
          <div className={styles.iconRecordsWrapper} key={icons.id}>
            <div
              className={cx(
                styles.iconRecordsBlock,
                state.selected.some(data => data.id === icons.id) &&
                  styles.mediaRecordsBlockSelected
              )}
              onClick={() => handleClick(icons)}
            >
              <IKImage
                id={`icons_image_${icons.id}`}
                className={styles.icon}
                path={icons.url || defaultContentImage}
              />
            </div>
            <TruncatedTextWithTooltip className={styles.iconName} content={icons?.name || ''} maxWidth={82} placement='top'/>
          </div>
          )
        })}
      </div>
      {showDeleteModal && (
        <DeleteModal
          showDeleteModal={true}
          message={`Are you sure you want to delete ${
            state?.selected?.length > 1
              ? `these ${state?.selected?.length} files`
              : "this file"
          }?`}
          title={`Delete ${state.active === "icons" && "Icons"}`}
          isDeleting={deleting}
          backdrop={deleting}
          cancelHandler={() => setShowDeleteModal(false)}
          handleDeleteClick={handleDelete}
        />
      )}
    </InfiniteScroll>
  );
};

function MediaUploadIndicator({state, upload, instance, onDimensionChange}) {

  const imageRef = React.useRef(null);

  const previewFile = (file) => {
    if (!imageRef.current) {
      return;
    }

    var reader = new FileReader();

    reader.onloadend = function () {
      if (imageRef.current) {
        imageRef.current.src = reader.result;
      }
    }

    if (file) {
      reader.readAsDataURL(file);
    } else {
      imageRef.current.src = "";
    }
  }


  useEffect(() => {
    if (instance) {
      const file = instance.files[0];

      if (state.active === 'icons' && imageRef?.current) {
        imageRef.current.onload = onImageLoad;
        previewFile(file);
      }
    }
  }, [imageRef]);

  const onImageLoad = () => {
    const dimension = {
      width: imageRef.current.naturalWidth,
      height: imageRef.current.naturalHeight
    }

    if (onDimensionChange) {
      onDimensionChange(dimension);
    }
  }

  return (
    <div
      className={cx(styles.iconBlock, styles.iconUploadingBlock)}
      key={upload.id}
    >
      <img ref={imageRef} style={{ display: "none" }}/>
      <div className={cx(styles.icon, styles.iconUploading)}>
        {!upload?.processing ? (
          <ProgressBar variant="warning" now={upload.progress} />
        ) : (
          <div style={{ fontSize: "12px" }}>Processing...</div>
        )}
      </div>
      <TruncatedTextWithTooltip className={styles.iconName} content={upload?.name || ''} maxWidth={82} placement='top'/>
    </div>
  )
}

const IconsUploadButton = ({ paletteId }) => {

  const dispatch = useDispatch();

  const handleUploadStart = (instances) => {
    instances.forEach(instance => {
      const upload = {
        id: instance.files[0].id,
        name: instance.files[0].name,
        type: instance.files[0].type,
        size: (instance.files[0].size / 1048576),
        progress: 0,
        instance: instance
      }

      dispatch(initiateBrandKitIconsUpload(upload));
    });
  }

  const handleUploadProgress = (file, progress) => {
    dispatch(updateBrandKitIconsUploadProgress(file.id, progress));
  }

  const handleUploadComplete = (info, file) => {
    const formats = ['image/jpg', 'image/jpeg', 'image/png', 'image/svg', 'image/bmp', 'image/gif', 'image/svg+xml', 'video/mp4']
    if (info && formats.includes(file.type)) {
      var request = {
        fileName: file.name,
        contentType: file.type,
        signedUrl: info.signedUrl,
        width: _.get(file, 'dimension.width'),
        height: _.get(file, 'dimension.height'),
        palette: paletteId,
        target: undefined,
      }

      if (file.type === 'video/mp4') {
        var video = document.createElement('video');

        video.onloadedmetadata = () => {
          request = {
            ...request,
            width: video.videoWidth,
            height: video.videoHeight
          }

          handleUploadIcons(request, file);
        };
        video.onerror = () => {

        };
        video.src = URL.createObjectURL(file);
      } else {
        handleUploadIcons(request, file);
      }
    }
  }

  const handleUploadIcons = (request, file) => {
    dispatch(uploadBrandKitIcons(file.id, request, (response) => {
    }));

    var mixpanelPayload = {
      brandkit_element_type: "Icons"
    }

    dispatch(createMixpanelEvent(MIXPANEL_BRANDKIT_CREATED, mixpanelPayload));
  }

  return (
    <FileUploadButton
      className={cx(styles.upload)}
      name="mediaFile"
      multiple={true}
      accept={[
              "image/jpg",
              "image/jpeg",
              "image/png",
              "image/svg",
              "image/bmp",
              "image/gif",
              "image/svg+xml"
            ]}
      onStart={handleUploadStart}
      onProgress={handleUploadProgress}
      onComplete={handleUploadComplete}
    >
      <Button variant="primary" className={styles.uploadButton}><i className="cleverstory-upload" />Upload Icons</Button>
    </FileUploadButton>
  )
}

export default IconElement;